import { useCallback, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';
import { debounce, isEqual } from 'lodash';
import {
  useGetCalculationsOnProductExpansesUpdate,
  useGetCalculationsTotalExpanses,
} from 'services/calculations/expenses/hooks';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { initialPeriodsTableRow } from 'components/BusinessToolForm/constants/periods';
import { useGlobalProducts } from 'components/BusinessToolForm/hooks/useGlobalProducts';
import { LOAN_REPAYMENT_SCHEDULE_NAMES } from 'components/BusinessToolForm/Sections/FinancialPlan/CashFlowElements/Sections/LoanRepaymentScheduleForm/utils/form';
import { loanRepaymentSchedulePrefix } from 'components/BusinessToolForm/Sections/FinancialPlan/CashFlowElements/utils/form';
import { IGlobalProductEntity } from 'components/BusinessToolForm/Sections/Global/general/products';
import {
  convertToOpposite,
  PERIODS_OPERATION_TYPES,
  summarizeSubtractPeriodValues,
} from 'components/BusinessToolForm/utils/helpers';

import { concatWithDot } from 'helpers/string';
import ToastService from 'helpers/ToastService';

import { createRequestBodyForMonthlyExpanses, getProductExpenses, getProductExpensesData } from './helper';
import { IFormContentTypes, observedFields } from '../../utils/form';

export const useTotalExpansesCalculation = () => {
  const previousProductContent = useRef<IGlobalProductEntity[]>([]);
  const shouldExpansesByProductUpdate = useRef(false);
  const loanPaymentTable = useRef([initialPeriodsTableRow]);
  const { id: businessPlanId = '' } = useParams();

  const { setValue, getValues } = useFormContext();
  const { products } = useGlobalProducts();

  loanPaymentTable.current = getValues(concatWithDot(loanRepaymentSchedulePrefix, LOAN_REPAYMENT_SCHEDULE_NAMES.TABLE));

  previousProductContent.current = getProductExpenses(products);

  const getUpdatedTotalCalculation = useGetCalculationsTotalExpanses();
  const getCalculationByProductExpanses = useGetCalculationsOnProductExpansesUpdate();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MARKETING_AND_SALES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MARKETING_AND_SALES.SALES_PLAN,
  );

  const [
    tableName,
    totalFieldName,
    costOfSales,
    otherOperatingExpansesField,
    interestPaidField,
    loanPaymentTableField,
    // lossOnSaleField,
    rAndDField,
    salesAndMarketingField,
    generalAndAdministrativeField,
    otherOperatingField,
    financialExpensesField,
    otherExpensesField,
    interestPaidIndirectField,
  ] = observedFields.expensesMonthly.setCalculatedValueFieldNames;

  const debouncedUpdateFunction = useMemo(
    () =>
      debounce(async (generalExpenseData, unit, productsExpanses) => {
        const params = {
          unit,
          businessPlanId,
          generalExpenseData,
        };

        try {
          if (shouldExpansesByProductUpdate.current) {
            const productExpensesData = getProductExpensesData(productsExpanses);
            const params = {
              unit,
              businessPlanId,
              expenseData: {
                expenseData: productExpensesData,
              },
            };
            await getCalculationByProductExpanses.mutateAsync(params);
            shouldExpansesByProductUpdate.current = false;
          }

          const { generalExpenseResponseData, variableCostOfSales, totalGeneralExpense, fixedCostOfSales } =
            await getUpdatedTotalCalculation.mutateAsync(params);
          const [costOfProduction, expansesTableDataWithoutCostOfProduction] = [
            generalExpenseResponseData[0].calculatedResponse,
            generalExpenseResponseData.slice(1),
          ];
          const expansesTableData = expansesTableDataWithoutCostOfProduction.map((row) => row.calculatedResponse);

          setValue(tableName, [variableCostOfSales, fixedCostOfSales, ...expansesTableData]);
          setValue(totalFieldName, totalGeneralExpense);
          setValue(costOfSales, costOfProduction);
          setValue(otherOperatingExpansesField, convertToOpposite(expansesTableData[3]));
          setValue(interestPaidField, convertToOpposite(expansesTableData[4]));
          setValue(loanPaymentTableField, [loanPaymentTable.current[0], expansesTableData[4]]);
          //TODO delete if client agreed
          // setValue(lossOnSaleField, expansesTableData[5]);
          setValue(rAndDField, expansesTableData[0]);
          setValue(salesAndMarketingField, expansesTableData[1]);
          setValue(generalAndAdministrativeField, expansesTableData[2]);
          setValue(otherOperatingField, expansesTableData[3]);
          setValue(financialExpensesField, expansesTableData[4]);
          setValue(otherExpensesField, generalExpenseData.nonOperatingExpense);
          setValue(interestPaidIndirectField, convertToOpposite(expansesTableData[4]));
        } catch (error) {
          if (error instanceof Error) ToastService.showError(error.message);
        }
      }, 300),

    [],
  );

  const salesPlanCalculationHandler = useCallback(
    async (content: IFormContentTypes['expensesMonthly']) => {
      if (!canAccess) return;

      const [currentMonthlyExpensesObject, operationUnit, products, investingActivitiesTable] = content;

      const productsExpanses = getProductExpenses(products);

      if (!isEqual(previousProductContent.current, productsExpanses)) {
        shouldExpansesByProductUpdate.current = true;
        previousProductContent.current = productsExpanses;
      }

      const nonOperatingExpense = summarizeSubtractPeriodValues(
        [...investingActivitiesTable.slice(2, 4)],
        PERIODS_OPERATION_TYPES.ADD,
      );
      const generalExpenseData = createRequestBodyForMonthlyExpanses(currentMonthlyExpensesObject, nonOperatingExpense);
      const unit = operationUnit ?? DIMENSIONALITY_VALUES.SINGLE;

      debouncedUpdateFunction(generalExpenseData, unit, productsExpanses);
    },

    [debouncedUpdateFunction],
  );

  return salesPlanCalculationHandler;
};
